import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import { PersonnelClient, JobsClient, PagedResultsOfAuditTrail, UserClient } from 'src/shared/api';
import {
	map,
	withLatestFrom,
	mapTo,
	filter,
	concatMap,
	tap
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { PersonnelDetailComponent } from '../personnel-detail.component';
import { PersonnelDetailPartialState } from './personnel-detail.reducer';
import { personnelFragments } from '../../personnel.routes';
import { personnelFormToFieldWorker } from '../personnel-detail.utility';
import * as PersonnelDetailActions from './personnel-detail.actions';
import * as PersonnelDetailSelectors from './personnel-detail.selectors';
import { FieldWorkerTypeId, getAllListArgs } from 'src/shared/constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';

@Injectable()
export class PersonnelDetailEffects {
	selectPersonnel$ = createEffect(() =>
		this.dataPersistence.navigation(PersonnelDetailComponent, {
			run: (
				r: ActivatedRouteSnapshot,
				_state: PersonnelDetailPartialState
			) => {
				const personnelId = grabIdFromParams(r.paramMap);

				return PersonnelDetailActions.personnelSelected({
					personnelId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadPersonnelFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PersonnelDetailActions.personnelSelected),
			map((action) => action.personnelId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(PersonnelDetailSelectors.getSelectedPersonnel)
				),
				(_id, personnel) => personnel
			),
			filter(Boolean),
			map((personnel) =>
				PersonnelDetailActions.personnelLoadedFromStore({ personnel })
			)
		)
	);

	loadPersonnelFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PersonnelDetailActions.personnelSelected),
			map((action) => action.personnelId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(PersonnelDetailSelectors.getSelectedPersonnel)
				)
			),
			filter(([_id, personnel]) => !personnel),
			concatMap(([id, _personnel]) =>
				this.personnelClient.getFieldWorker(id).pipe(
					map((personnel) =>
						PersonnelDetailActions.personnelLoadedFromApi({
							personnel,
						})
					)
				)
			)
		)
	);

	loadAssignments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				PersonnelDetailActions.personnelLoadedFromApi,
				PersonnelDetailActions.personnelLoadedFromStore),
			map((action) => action.personnel),
			filter(
				(personnel) =>
					personnel.type.id !== FieldWorkerTypeId.SupportTypeId
			),
			map((personnel) => personnel.id),
			concatMap((fieldWorkerId) =>
				this.jobsClient.getAssignments({
					...getAllListArgs,
					fieldWorkerId,
				})
			),
			map((pagedResults) => pagedResults.results),
			map((assignments) =>
				PersonnelDetailActions.assignmentsLoaded({ assignments })
			)
		)
	);

	startNewPersonnel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PersonnelDetailActions.personnelSelected),
			map((action) => action.personnelId),
			filter((id) => id === 0),
			mapTo(PersonnelDetailActions.newPersonnelStarted())
		)
	);

	savePersonnel$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			PersonnelDetailActions.saveRequested,
			{
				run: (
					action: ReturnType<
						typeof PersonnelDetailActions.saveRequested
					>,
					_state: PersonnelDetailPartialState
				) => {
					const personnel = personnelFormToFieldWorker(
						action.formValue
					);

					if (personnel.status.description == "Terminated") {
						this.userClient.unlinkFieldWorker(personnel.id).subscribe(
							() => {
							  this.store.dispatch(
								PersonnelDetailActions.terminateFieldWorker({
								  fieldWorkerId: personnel.id,
								})
							  );
							},
						)
					}

					return this.personnelClient.saveFieldWorker(personnel).pipe(
						map((id) =>
							personnel.id === 0
								? PersonnelDetailActions.newPersonnelSaved({
									personnel: { ...personnel, id },
								})
								: PersonnelDetailActions.personnelSaved({
									personnel,
								})
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof PersonnelDetailActions.saveRequested
					>,
					error: any
				) => {
					return PersonnelDetailActions.errorSavingPersonnel({
						error,
					});
				},
			}
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					PersonnelDetailActions.errorSavingPersonnel,
					PersonnelDetailActions.errorLoadingPersonnel
				),
				map((action) => action.error),
				map((error) => {
					throw error;
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(PersonnelDetailActions.personnelSaved),
				withLatestFrom(
					this.store.pipe(
						select(PersonnelDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(personnelFragments.list))
			),
		{ dispatch: false }
	);

	navigateToCreatedPersonnel$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(PersonnelDetailActions.newPersonnelSaved),
				map((action) => action.personnel.id),
				tap((id) => this.router.navigate(personnelFragments.detail(id)))
			),
		{ dispatch: false }
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(PersonnelDetailActions.personnelSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	requestPersonnelHistory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PersonnelDetailActions.personnelSelected, PersonnelDetailActions.personnelSaved),
			withLatestFrom(
				this.store.pipe(
					select(PersonnelDetailSelectors.getSelectedPersonnelId)
				),
				(_, selectedId) => selectedId
			),
			filter((id) => id > 0),
			map(() => PersonnelDetailActions.historyRequested())
		)
	);

	loadPersonnelHistoryFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PersonnelDetailActions.historyRequested),
			withLatestFrom(
				this.store.pipe(
					select(PersonnelDetailSelectors.getSelectedPersonnelId)
				),
				(_, selectedId) => selectedId
			),
			filter((id) => id > 0),
			concatMap((id) =>
				this.personnelClient.getFieldWorkerHistory(id).pipe(
					map((pagedHistoryResults: PagedResultsOfAuditTrail) => PersonnelDetailActions.historyLoaded({ pagedHistoryResults }))
				)
			)
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<PersonnelDetailPartialState>,
		private readonly dataPersistence: DataPersistence<
			PersonnelDetailPartialState
		>,
		private readonly personnelClient: PersonnelClient,
		private readonly jobsClient: JobsClient,
		private readonly userClient: UserClient,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar
	) { }
}
